import { getMetricLabel } from '@vizit/constants';
import * as React from 'react';
import { BaseMetricCard } from '../base-metric-card';
import { BarChart } from '../metric-charts/bar-chart';
import type { BarMetric } from '../metric-charts/types';

interface AssetCountMetricCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The bar metric data to display
   */
  metric: BarMetric;
  /**
   * Optional description text to display below the title
   */
  description?: string;
  /**
   * Width of the chart
   * @default 300
   */
  chartWidth?: number;
  /**
   * Height of the chart
   * @default 60
   */
  chartHeight?: number;
  /**
   * Number of filled zaps for impact badge (0-4)
   */
  filledZapCount: number;
  /**
   * Whether the card is in loading state
   * @default false
   */
  isLoading?: boolean;
}

/**
 * AssetCountMetricCard displays an asset count metric with bar chart and impact badge
 */
const AssetCountMetricCard = React.forwardRef<HTMLDivElement, AssetCountMetricCardProps>((props, forwardedRef) => {
  const {
    metric,
    description,
    chartWidth = 300,
    chartHeight = 60, // score-threshold-allow: chart height in px
    filledZapCount,
    isLoading = false,
    ...other
  } = props;

  const [isHovered, setIsHovered] = React.useState(false);
  const label = getMetricLabel(metric.key);

  // Calculate positions for the range labels based on the metric data
  const { range, optimalBand } = metric;
  const bandMin = Math.min(...optimalBand);
  const bandMax = Math.max(...optimalBand);
  const rangeSpan = range.max - range.min;

  // Calculate percentage positions for each value
  const bandMinPercent = ((bandMin - range.min) / rangeSpan) * 100;
  const bandMaxPercent = ((bandMax - range.min) / rangeSpan) * 100;

  // Check if optimal band boundaries match range boundaries to avoid duplicates
  const showBandMin = bandMin !== range.min;
  const showBandMax = bandMax !== range.max;

  return (
    <BaseMetricCard
      ref={forwardedRef}
      label={label}
      description={description}
      filledZapCount={filledZapCount}
      isLoading={isLoading}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      footer={
        <div
          className={`relative w-full text-xs text-gray-utility-500 h-[1.2em] ${isHovered ? 'opacity-100' : 'opacity-0'}`}
        >
          {/* Range labels */}
          <div className="flex justify-between items-center h-full">
            <span className="absolute left-0">{range.min}</span>
            {showBandMin && (
              <span className="absolute" style={{ left: `${bandMinPercent}%`, transform: 'translateX(-50%)' }}>
                {bandMin}
              </span>
            )}
            {showBandMax && (
              <span className="absolute" style={{ left: `${bandMaxPercent}%`, transform: 'translateX(-50%)' }}>
                {bandMax}
              </span>
            )}
            <span className="absolute right-0">{range.max}</span>
          </div>
          {/* Dotted line connecting optimal band values */}
          <div
            className="absolute top-1/2 border-t border-dotted border-gray-utility-300"
            style={{
              left: `calc(${bandMinPercent}% + 0.35em)`,
              width: `calc(${bandMaxPercent - bandMinPercent}% - 0.7em)`,
            }}
          />
        </div>
      }
      {...other}
    >
      <BarChart metric={metric} width={chartWidth} height={chartHeight} />
    </BaseMetricCard>
  );
});

AssetCountMetricCard.displayName = 'AssetCountMetricCard';

export type { AssetCountMetricCardProps };
export { AssetCountMetricCard };
